<template>
  <div class="company-annual-report">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>企业年报</span>
        </div>
      </template>

      <!-- 查询表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="年份">
          <el-date-picker
            v-model="queryParams.year"
            type="year"
            placeholder="选择年份"
            format="YYYY"
            value-format="YYYY"
            style="width: 200px"
            @change="handleSearch"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 报表内容 -->
      <div v-loading="loading" class="report-content">
        <el-card v-if="reportData" shadow="never">
          <template #header>
            <div class="report-header">
              <span>企业年报 - {{ reportData.year }}年</span>
            </div>
          </template>

          <!-- 企业信息 -->
          <div v-if="reportData.companyInfo" class="company-info-section">
            <h3>企业信息</h3>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="企业名称">
                {{ reportData.companyInfo.companyName || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="统一社会信用代码">
                {{ reportData.companyInfo.creditCode || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="企业类型">
                {{ reportData.companyInfo.companyType || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="成立日期">
                {{ formatDate(reportData.companyInfo.establishDate) }}
              </el-descriptions-item>
            </el-descriptions>
          </div>

          <!-- 财务汇总 -->
          <el-row :gutter="16" class="summary-row">
            <el-col :span="8">
              <el-card class="summary-card">
                <div class="summary-label">总收入</div>
                <div class="summary-value revenue">¥{{ formatAmount(reportData.totalRevenue) }}</div>
              </el-card>
            </el-col>
            <el-col :span="8">
              <el-card class="summary-card">
                <div class="summary-label">总成本</div>
                <div class="summary-value cost">¥{{ formatAmount(reportData.totalCost) }}</div>
              </el-card>
            </el-col>
            <el-col :span="8">
              <el-card class="summary-card">
                <div class="summary-label">净利润</div>
                <div class="summary-value profit">¥{{ formatAmount(reportData.totalProfit) }}</div>
              </el-card>
            </el-col>
          </el-row>

          <!-- 销售报表 -->
          <div v-if="reportData.salesReport" class="sales-report-section">
            <h3>销售报表</h3>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="销售总额">
                {{ reportData.salesReport.totalAmount ? '¥' + formatAmount(reportData.salesReport.totalAmount) : '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="销售订单数">
                {{ reportData.salesReport.totalCount || 0 }}
              </el-descriptions-item>
            </el-descriptions>
          </div>

          <!-- 提示信息 -->
          <el-alert
            v-if="!reportData.companyInfo && !reportData.salesReport"
            title="暂无数据"
            type="info"
            :closable="false"
            style="margin-top: 20px"
          >
            <template #default>
              <p>该年份暂无报表数据，请稍后再试。</p>
            </template>
          </el-alert>
        </el-card>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { getCompanyAnnualReport } from '../api/accountant'

// 数据
const loading = ref(false)
const reportData = ref(null)
const queryParams = reactive({
  year: null
})

// 方法
const formatDate = (date) => {
  if (!date) return '-'
  if (typeof date === 'string') {
    return date.substring(0, 10)
  }
  return date
}

const formatAmount = (amount) => {
  if (!amount) return '0.00'
  return Number(amount).toFixed(2)
}

const loadReport = async () => {
  loading.value = true
  try {
    const params = {}
    if (queryParams.year) {
      params.year = parseInt(queryParams.year)
    }
    const res = await getCompanyAnnualReport(params)
    if (res.code === '000000') {
      reportData.value = res.data
    } else {
      ElMessage.error(res.msg || '获取企业年报失败')
    }
  } catch (error) {
    console.error('获取企业年报失败:', error)
    ElMessage.error('获取企业年报失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  loadReport()
}

const handleReset = () => {
  queryParams.year = null
  loadReport()
}

// 生命周期
onMounted(() => {
  // 默认查询当前年份的报表
  const currentYear = new Date().getFullYear()
  queryParams.year = currentYear.toString()
  loadReport()
})
</script>

<style scoped>
.company-annual-report {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
}

.search-form {
  margin-bottom: 20px;
}

.report-content {
  margin-top: 20px;
}

.report-header {
  font-weight: 600;
  font-size: 16px;
}

.company-info-section,
.sales-report-section {
  margin-bottom: 20px;
}

.company-info-section h3,
.sales-report-section h3 {
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: 600;
}

.summary-row {
  margin-bottom: 20px;
}

.summary-card {
  text-align: center;
}

.summary-label {
  color: #909399;
  font-size: 14px;
  margin-bottom: 10px;
}

.summary-value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.summary-value.revenue {
  color: #67c23a;
}

.summary-value.cost {
  color: #e6a23c;
}

.summary-value.profit {
  color: #409eff;
}
</style>

